import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, ViewChild, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MenuItem } from "@openng/optimus-ui/api";
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { firstValueFrom } from 'rxjs';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { UserInteractionItem } from 'src/modules/utils/misc/user-interaction-item.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { ComponentView } from '../../models/component-view.model';
import { MainAppService } from '../../services/main-app.service';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { DeveloperAction } from 'src/modules/enaio/netgen/DeveloperAction';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { CssDebugger } from 'src/modules/sm-base/misc/css-debugger.model';

@Component({
    selector: 'app-main',
    styleUrls: ['./app-main.component.scss'],
    templateUrl: './app-main.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppMainComponent implements AfterViewInit {

    static instance: AppMainComponent;

    app = inject(MainAppService);
    http = inject(HttpClient);
    router = inject(Router);
    zone = inject(NgZone);

    inited = false;
    sidebarActive: boolean;
    activeSidebarTabIndex: number;
    browserSupported = true;
    privacyPolicyAccepted = false;
    privacyPolicy = false;
    privacyPolicyHtml: any;
    lastMouseDown: Date;
    currentComponentFullScreen = true;
    debugCssTimer: SmTimer = null;
    debugMessageText: string = null;

    @ViewChild(RouterOutlet) outlet!: RouterOutlet;
    @ViewChild("cm") cm: ContextMenu;
    @ViewChild('confirmPopupTarget') confirmPopupTargetRef: ElementRef;

    constructor() {
        AppMainComponent.instance = this;
        this.browserSupported = this.app.deviceDetector.browser() != "internetexplorer";
        this.privacyPolicy = this.app.privacyPolicy;
    }

    onMouseMove: (e: PointerEvent) => void;

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            this.onMouseMove = (e: PointerEvent) => this.app.setCurrentMousePos(e);

            window.addEventListener('pointermove', this.onMouseMove, { passive: true });
        });
    }

    ngOnDestroy(): void { 
        window.removeEventListener('pointermove', this.onMouseMove);
    }

    async ngAfterViewInit(): Promise<void> {
        if (!this.browserSupported) {
            return;
        }

        this.router.events.subscribe(() => {
            GuiUtils.angularTimer(() => this.updateFullScreenStatus());
        });

        if (this.privacyPolicy) {
            this.privacyPolicyAccepted = this.app.auth.userDetails?.getDatabaseUser()?.privacyPolicyAccepted;
            this.privacyPolicyHtml = this.privacyPolicyAccepted ? "" : await firstValueFrom(this.http.get("assets/portal/templates/privacyPolicy.html", { responseType: "text"}));
        }

        GuiUtils.angularTimer(() => {
            this.inited = true;
        });
    }

    ngDoCheck(): void {
        this.app.changeDetectionCount++;
    }

    onHotkeyInspectComponent(): void {
        if ((window as any).ng == null) {
            console.log("Das funktioniert nur im Debug-Modus. window.ng ist nicht definiert.");
            return;
        }

        let nodes = document.querySelectorAll(":hover");
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < nodes.length; i++) {
            let p = (window as any).ng.getComponent(nodes[i]);
            if (p != null) {
                console.log(p);
            }
        }
    }

    onHotkeyDeveloper(): void {
        let model: MenuItem[] = [
            {
                label: "Beta-Funktionen",
                badge: this.app.isBeta ? "AN" : "AUS",
                command: () => {
                    this.app.setBeta(!this.app.isBeta);
                }
            },
            {
                label: "Erweitere Dev-Optionen",
                badge: this.app.advancedDeveloper ? "AN" : "AUS",
                command: () => {
                    this.app.advancedDeveloper = !this.app.advancedDeveloper;
                }
            },
            {
                label: "Sidebar-Anzeige umschalten",
                badge: this.app.showSidebar ? "AN" : "AUS",
                command: () => {
                    this.app.showSidebar = !this.app.showSidebar;
                }
            },
            {
                label: "Kompakter Modus",
                badge: this.app.compactGui? "AN" : "AUS",
                command: () => {
                    this.app.setCompactGui(!this.app.compactGui);
                }
            },
            {
                label: "Angular-Routen debuggen",
                badge: this.app.debugRoutes ? "AN" : "AUS",
                command: () => {
                    this.app.debugRoutes = !this.app.debugRoutes;
                }
            },
            {
                label: "Fokus verfolgen",
                badge: this.app.useFocusTracker ? "AN" : "AUS",
                command: () => {
                    this.app.setUseFocusTracker(!this.app.useFocusTracker);
                }
            },
            {
                label: "Dark-Mode an/aus",
                badge: this.app.darkMode === true ? "AN" : this.app.darkMode === false ? "AUS" : "AUTO",
                command: () => {
                    this.app.toggleDarkMode(true);
                }
            },
            {
                label: "Interaktions-Historie auf Konsole ausgeben",
                command: () => {
                    console.log("\r\n"+ Arrays.itemsToString(Arrays.reverse(Arrays.sort(this.app.userInteractionHistory)).map(item => item.toString()), "\r\n"));
                }
            },
            {
                label: "Fehler werfen",
                command: () => {
                    throw new Error();
                }
            },
            {
                label: "CSS Debugging",
                badge: this.debugCssTimer != null ? "AN" : "AUS",
                command: () => {
                    if (this.debugCssTimer != null) {
                        this.debugCssTimer.destroy();
                        this.debugCssTimer = null;
                        this.debugMessageText = null;
                    }
                    else {
                        this.debugCssTimer = new SmTimer("debugCssTimer", 0, 1000, () => this.debugCss(), true);
                    }
                }
            },
            {
                label: "Angular ChangeDetection Debugging",
                command: () => {
                    let oldConsole = window.console.log;
                    window.console.log = (data: any[]) => {
                        oldConsole(data);
                        if (Strings.is(data)) {
                            data = [data];
                        }
                        if (data.length > 0 && Strings.is(data[0]) && (data[0] as string).endsWith("ms per check")) {
                            this.app.showToast("info", "Change Detection Analyse", data[0] as string);
                        }
                    };
                    Utils.setTimerOnce(1000, () => {
                        window.console.log = oldConsole;
                    });
                    (window as any).ng.profiler.timeChangeDetection();
                }
            },
            {
                label: "Angular ChangeDetection Debugging Periodic",
                command: () => {
                   Utils.setTimer(1000, () => console.log("Anzahl ChangeDetections: " + this.app.changeDetectionCount));
                }
            },
            {
                label: "Interne Prüfungen starten",
                command: async () => {
                    let result = await RestEndpoint.main().run("api/app/runInternalChecks").listStrings();
                    this.app.dialog.textArea("Interne Prüfungen", Arrays.itemsToString(result, "\r\n"), null, true);
                }
            },
            {
                label: "Bug Report zur Zwischenablage",
                visible: this.app.mainConfig.lastBugreportAvailable,
                command: async () => {
                    let report = await RestEndpoint.main().run("api/app/getLastBugreport").getText();
                    if (Utils.isNoe(report)) {
                        this.app.showToast("info", "Bug Report", "Es ist kein gespeicherter Bug Report vorhanden");
                        return;
                    }
                    await this.app.copyToClipboardOrDialog(report, "Bug Report", "Bug Report wurde in die Zwischenablage kopiert");
                }
            },
            {
                label: "Developer Action",
                visible: this.app.mainConfig.developerActionsAvailable,
                command: async () => {
                    let actions = await RestEndpoint.main().run("api/app/developerAction").list(DeveloperAction);
                    let form = new FrontendForm([
                        new FrontendField("type", "Typ", FrontendFieldType.dropdown, {
                            listItems: FrontendFieldListItem.make(actions, item => [item, item.name]),
                            mandatory: true,
                            onValueChanged: () => {
                                let action = form.getValueObject("type", DeveloperAction);
                                form.fields = [form.fields[0], ...action?.form != null ? action.form.fields : []];
                            }
                        })
                    ]);
                    if (await this.app.dialog.form(form, "Aktion konfigurieren") == "ok") {
                        let rawResult = await RestEndpoint.main().post().query({id: form.getValueObject("type", DeveloperAction).id}).body(form.getAsObject()).run("api/app/developerAction").getString();
                        let result = Objects.fromJson(rawResult);
                        this.app.dialog.textArea("Ergebnis", Strings.is(result) ? result as string : rawResult);
                    }
                }
            }
        ];
        this.app.openContextMenu(model);
    }

    debugCss(): void {
        let found = new CssDebugger().debug();
        this.debugMessageText = !Utils.isNoe(found) ? Arrays.itemsToString(Arrays.sort(found), "<br>") : "Keine CSS-Probleme gefunden";
    }

    onHotkeyOnSelection(): void {
        if (this.app.onSelectionCallback != null) {
            this.app.onSelectionCallback();
        }
    }

    @HostListener('document:click', ['$event'])
    async onClick(event: Event): Promise<void> {
        if (event.target != null) {
            let debugString = event.target instanceof HTMLElement ? GuiUtils.getHtmlElementDebugString(event.target) : [Objects.toString(event.target)];
            this.app.userInteractionHistory.push(new UserInteractionItem(this.lastMouseDown ?? new Date(), "CLICK", debugString));
            this.lastMouseDown = null;
        }
        if (event.target instanceof HTMLAnchorElement && event.target.className === 'dynamic-router-link') {
            event.preventDefault();
            const route = event.target?.getAttribute('href');
            if (route) {
                await this.app.navigateTo([`/${route}`]);
            }
        }
    }

    @HostListener("document:mousedown", ["$event"])
    public onMouseDown(event: Event): boolean {
        this.app.userInteracted();

        if (event.target != null) {
            this.lastMouseDown = new Date();
        }
        return true;
    }

    public onKeyPress(event: KeyboardEvent): boolean {
        let key = this.app.userKeyPress(event);
        //console.log(key, event.key, event.code, event.altKey, event.shiftKey)
        if (key == "SHIFT-ALT-Y") {
            this.onHotkeyInspectComponent();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-D") {
            this.onHotkeyDeveloper();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-C") {
            this.onHotkeyOnSelection();
            event.stopPropagation();
            event.preventDefault();
        }
        return true;
    }

    @HostListener("document:wheel", ["$event"])
    public onWheel(_: Event): boolean {
        this.app.userInteracted();
        return true;
    }

    @HostListener("document:touchstart", ["$event"])
    public onTouchStart(event: TouchEvent): boolean {
        this.app.userInteracted();

        const [touch] = event.touches ?? [null];
        if (touch) {
            this.app.setCurrentMousePos(touch);
        }

        return true;
    }

    @HostListener("document:scroll", ["$event"])
    public onScroll(_: Event): boolean {
        this.app.userInteracted();
        return true;
    }

    async onTabClick(event, index: number): Promise<void> {
        if (this.app.visibleMenuTabs[index].routerLink != null) {
            this.activeSidebarTabIndex = index;
            this.sidebarActive = false;
            await this.app.navigateTo(this.app.visibleMenuTabs[index].routerLink);
        }
        else if (this.activeSidebarTabIndex === index) {
            this.sidebarActive = !this.sidebarActive;
        }
        else {
            this.activeSidebarTabIndex = index;
            this.sidebarActive = true;
        }

        event.preventDefault();
    }

    closeSidebar(event: Event): void {
        this.sidebarActive = false;
        event.preventDefault();
    }

    onTopbarSubItemClick(event: Event): void {
        event.preventDefault();
    }

    isDesktop(): boolean {
        return window.innerWidth > 1024;
    }

    updateFullScreenStatus(): void {
        try {
            const activated = this.outlet?.component;
            this.currentComponentFullScreen = activated instanceof ComponentView && activated.fullScreenComponent;
        }
        catch (error: any) {
            //User befindet sich zum Beispiel im Login-Dialog
            if (error.message != "NG04012" &&
                error.message != "NG04012: Outlet is not activated") {
                throw error;
            }
        }
    }

    async acceptPrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            user.privacyPolicyAccepted = true;
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/acceptPrivacyPolicy").getText();
        }
        this.privacyPolicyAccepted = true;
    }

    async declinePrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/declinePrivacyPolicy").getText();
        }
        await this.app.auth.logout();
    }

    toastClosed(event: any): void {
        if (event.message.data?.onClose != null) {
            event.message.data?.onClose(event.message.data?.id);
        }
    }
}
